import React, { useContext, useEffect, useMemo, useReducer, useState } from 'react';
import { useSnackbar } from 'notistack';

import { useParams } from 'react-router-dom';
import { GarmentObjectGarmentPage } from '../../types/reducers/filterReducer';
import { GlobalContext } from '../GlobalContext/GlobalContextProvider';
import { getSecondsDifference } from '../../utils/timeFunctions';
import {
  getCostsLogFromLocalStorage,
  getNewTeeTypesRowsFromLocalStorage,
  getVendorsFromLocalStorage,
  getTeeMeasuresAveragesRowsFromLocalStorage,
  getMaterialsFromLocalStorage,
  getMatchingColorsFromLocalStorage,
  getUniqueColorsFromLocalStorage,
} from '../../utils/localStorageActions';
import { VendorsRow } from '../../types/databaseTables/vendors';
import {
  createGarmentObjectFromId,
  getArrayOfAvailableSizes,
  getThisVendorWebsite,
  MatchingColorsObj,
} from './garmentContextFunctions';
import { GarmentPricesLogRow } from '../../types/databaseTables/garmentpriceslog';
import { TeeMeasuresAveragesRow } from '../../types/databaseTables/teemeasuresaverages';
import { UserContext } from '../UserContext/UserContextProvider';
import {
  garmentObjectReducer,
  GarmentObjectReducerAction,
} from '../../Reducers/garmentObjectReducer';
import { NewTeeTypesRow } from '../../types/databaseTables/newteetypes';

export interface CurrentSelection {
  size: string;
  color: string;
  thisTeeTypeRow: NewTeeTypesRow | undefined;
}

export interface GarmentPageGeneralData {
  vendors: VendorsRow[];
  costsLogArray: GarmentPricesLogRow[];
  teeMeasuresAverages: TeeMeasuresAveragesRow[];
  teeTypesArray: NewTeeTypesRow[];
  matchingColors: MatchingColorsObj;
  materialsArray: string[];
  uniqueColors: string[];
}

interface GarmentContextInterface {
  garmentObjectState: GarmentObjectGarmentPage;
  currentSelectionState: CurrentSelection;
  setCurrentSelectionState: React.Dispatch<React.SetStateAction<CurrentSelection>>;
  garmentPageGeneralDataState: GarmentPageGeneralData;
  dispatchGarmentObjectState: React.Dispatch<GarmentObjectReducerAction>;
}
export const GarmentContext = React.createContext<GarmentContextInterface>(
  {} as GarmentContextInterface
);

export default function GarmentContextProvider(props: { children: React.ReactNode }) {
  const { enqueueSnackbar } = useSnackbar();
  const params = useParams();
  const thisTeeTypeId = parseInt(params.garmenttypeid || '0', 10);
  const teeMeasureAveragesFromInitialLocalStorage = getTeeMeasuresAveragesRowsFromLocalStorage();
  const teeTypeRowsFromLocalStorage = getNewTeeTypesRowsFromLocalStorage();
  // USEMEMO
  const thisPageLoadTimestamp = useMemo(() => new Date().toUTCString(), []);

  // USECONTEXTS
  const { timestampsState, fetchGet, dispatchTimestampState } = useContext(GlobalContext);
  const { userDetailsState } = useContext(UserContext);
  // USESTATES
  const [garmentPageGeneralDataState, setGarmentPageGeneralDataState] =
    useState<GarmentPageGeneralData>({
      vendors: getVendorsFromLocalStorage(),
      costsLogArray: getCostsLogFromLocalStorage(),
      teeMeasuresAverages: teeMeasureAveragesFromInitialLocalStorage,
      teeTypesArray: teeTypeRowsFromLocalStorage,
      matchingColors: getMatchingColorsFromLocalStorage(),
      materialsArray: getMaterialsFromLocalStorage(),
      uniqueColors: getUniqueColorsFromLocalStorage(),
    });
  const createdGarmentObjectFromId = useMemo(
    () =>
      createGarmentObjectFromId(
        thisTeeTypeId,
        teeTypeRowsFromLocalStorage,
        teeMeasureAveragesFromInitialLocalStorage,
        garmentPageGeneralDataState.vendors,
        userDetailsState.fitScoreWeightings,
        userDetailsState.idealGarmentMeasurements
      ),
    []
  );
  // USEREDUCER
  const [garmentObjectState, dispatchGarmentObjectState] = useReducer(
    garmentObjectReducer,
    createdGarmentObjectFromId
  );
  const [currentSelectionState, setCurrentSelectionState] = useState<CurrentSelection>({
    size:
      params.size ||
      getArrayOfAvailableSizes(
        teeMeasureAveragesFromInitialLocalStorage,
        garmentObjectState.newteetypes_id
      )[0] ||
      '',
    color: garmentObjectState.availablecolors[0],
    thisTeeTypeRow: teeTypeRowsFromLocalStorage.find(
      (thisRow) => thisRow.newteetypes_id === thisTeeTypeId
    ),
  });

  // Update the vendors state and localStorage on page load, if we haven't updated recently
  useEffect(() => {
    // return;
    // If we've fetched the vendors array within the last 5 seconds then do nothing.
    const secondsSinceLastFetch = getSecondsDifference(
      timestampsState.fetch.vendors,
      new Date().toUTCString()
    );
    if (secondsSinceLastFetch > 5 * 60 || secondsSinceLastFetch < 0) {
      fetchGet('/general/data')
        .then((response) => {
          if (response.error === true) {
            enqueueSnackbar(`Error trying to fetch general/data`, { variant: 'error' });
          }
          // Extract data objects/arrays
          const { generalData, vendorsData } = (response as { body: unknown }).body as unknown as {
            generalData: Array<generalDataArrayRows>;
            vendorsData: Array<VendorsColumns>;
          };
          const matchingcolors = generalData.filter((x) => x.description === 'matchingcolors')[0]
            .jsonobject as MatchingColorsObj;
          const uniqueColors = generalData.filter((x) => x.description === 'uniquecolors')[0]
            .jsonobject as string[];
          const materials = generalData.filter((x) => x.description === 'materials')[0]
            .jsonobject as string[];

          // update localStorage
          localStorage.setItem('vendors', JSON.stringify(vendorsData));
          localStorage.setItem('matchingcolors', JSON.stringify(matchingcolors));
          localStorage.setItem('uniqueColors', JSON.stringify(uniqueColors));
          localStorage.setItem('materials', JSON.stringify(materials));
          // update State
          setGarmentPageGeneralDataState((prev) => ({
            ...prev,
            vendors: vendorsData,
            uniqueColors,
            materialsArray: materials,
            matchingColors: matchingcolors,
          }));
          const updatedVendorWebsite =
            getThisVendorWebsite(garmentObjectState.manufacturer, vendorsData) || '';
          if (updatedVendorWebsite !== garmentObjectState.vendorsWebsite) {
            dispatchGarmentObjectState({
              type: 'update vendor website',
              payload: {
                vendorsWebsite: updatedVendorWebsite || '',
              },
            });
          }
          // Update timestamps
          dispatchTimestampState({
            type: 'update fetch timestamps',
            payload: {
              labels: ['vendors', 'uniqueColors', 'materials', 'matchingcolors'],
            },
          });
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error(error);
          enqueueSnackbar(`Error trying to fetch vendorData`, { variant: 'error' });
        });
    }
  }, []);

  // If fetch requests have happened recently then we want to update the relevant state
  useEffect(() => {
    const secondsSinceThisPageLoaded = getSecondsDifference(
      thisPageLoadTimestamp,
      new Date().toUTCString()
    );

    const secondsSinceLastFetchCostsLog = getSecondsDifference(
      timestampsState.fetch.costslog,
      new Date().toUTCString()
    );
    if (secondsSinceLastFetchCostsLog < 5 && secondsSinceThisPageLoaded > 5) {
      setGarmentPageGeneralDataState((prev) => ({
        ...prev,
        costsLogArray: getCostsLogFromLocalStorage(),
        teeMeasuresAverages: getTeeMeasuresAveragesRowsFromLocalStorage(),
      }));
    }
  }, [timestampsState.fetch.vendors, timestampsState.fetch.costslog]);

  // If newTeeTypes are updated in fetch, then update the local garmentObjectState
  useEffect(() => {
    if (garmentObjectState.teemeasuresaverages_id === 0) {
      enqueueSnackbar(
        `We can't find a garment for this page right now. teemeasuresID = ${garmentObjectState.teemeasuresaverages_id}`,
        {
          variant: 'warning',
        }
      );
    }
    // If we've fetched the newTeeTypes array within the last 5 seconds then update the garmentObject (else we'll just stick with whatever was in localStorage)
    const secondsSinceLastFetch = getSecondsDifference(
      timestampsState.fetch.newTeeTypes,
      new Date().toUTCString()
    );

    if (secondsSinceLastFetch < 5) {
      enqueueSnackbar(`updating garmentObject from localStorage`, {
        variant: 'info',
      });

      dispatchGarmentObjectState({
        type: 'update garment object',
        payload: {
          thisTeeTypesRow: currentSelectionState.thisTeeTypeRow,
          teeMeasuresArray: garmentPageGeneralDataState.teeMeasuresAverages,
          vendors: garmentPageGeneralDataState.vendors,
          userFitScoreWeights: userDetailsState.fitScoreWeightings,
          idealGarmentMeasurements: userDetailsState.idealGarmentMeasurements,
          size: currentSelectionState.size,
        },
      });
    }
  }, [timestampsState.fetch.newTeeTypes]);

  useEffect(() => {
    dispatchGarmentObjectState({
      type: 'update garment object',
      payload: {
        thisTeeTypesRow: currentSelectionState.thisTeeTypeRow,
        teeMeasuresArray: garmentPageGeneralDataState.teeMeasuresAverages,
        vendors: garmentPageGeneralDataState.vendors,
        userFitScoreWeights: userDetailsState.fitScoreWeightings,
        idealGarmentMeasurements: userDetailsState.idealGarmentMeasurements,
        size: currentSelectionState.size,
      },
    });
  }, [currentSelectionState.size]);

  const value = React.useMemo(
    () => ({
      garmentObjectState,
      dispatchGarmentObjectState,
      currentSelectionState,
      setCurrentSelectionState,
      garmentPageGeneralDataState,
      setGarmentPageGeneralDataState,
    }),
    [garmentObjectState, currentSelectionState, garmentPageGeneralDataState]
  );

  return <GarmentContext.Provider value={value} {...props} />;
}
